import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { saveSession, saveFullSession, getGroqKey, saveGroqKey, loadSession } from "../History";

if (typeof window !== "undefined") {
  window.speechSynthesis.onvoiceschanged = () => {
    window.speechSynthesis.getVoices();
  };
}

const FILLER_WORDS = ["um", "uh", "er", "ah", "like", "you know", "i mean", "basically", "literally", "actually", "right", "so", "well", "kind of", "sort of", "anyway", "honestly", "obviously", "totally", "seriously"];

const SYSTEM_PROMPT = `You are a senior panel member conducting a real scholarship interview at a prestigious university. You are experienced, exacting, warm but unsparing in your assessments. Your evaluations must read like genuine panel notes — written directly to the candidate using "you" and "your", never referring to them as "the candidate".

You will receive the scholarship name, university, candidate name, field of study, their spoken transcript, and speech metrics.

QUESTION VARIETY RULES — CRITICAL:
- You have a large bank of question categories. Each question MUST come from a DIFFERENT category than the previous one.
- Categories: [opening/motivation, academic achievement, leadership, community/social impact, adversity/resilience, future vision, values/ethics, scholarship fit, critical thinking, personal character]
- Never ask two questions from the same category in one session.
- Questions must feel spontaneous and natural — never generic or template-sounding.
- Vary the opening phrasing: "Tell me about...", "Walk me through...", "In your view...", "How have you...", "What would you say...", "Can you describe...", "I'd be interested to hear...", "Reflecting on your journey..."
- Make questions specific to the scholarship name, university, and field of study provided.

EVALUATION RULES — CRITICAL:
- ALL feedback must address the candidate directly as "you/your" — never "the candidate".
- contentFeedback: Write 4-6 sentences. Be a real panel member. Identify exactly what was said well and what was weak or missing. Quote specific phrases from the transcript. Assess depth, structure, use of evidence, and persuasiveness. Do NOT be vague or generic.
- grammarFeedback: Write 2-3 sentences. Quote specific grammatical errors or awkward phrasing directly from the transcript. If grammar was strong, note what made it effective.
- suggestedAnswer: Write a model answer (5-7 sentences) showing how an ideal candidate would have answered this question. It should be specific, structured, and compelling — not generic.
- improvementTips: Give exactly 3 concrete, actionable tips the candidate can practise before their next interview. Be specific — not "be more confident" but "begin your answer with a one-sentence thesis before expanding, e.g. 'My motivation stems from three years of...'".
- overallImpression: 2 sentences. Honest panel-level verdict on this specific answer, addressed directly to the candidate.

Respond ONLY with this exact JSON — no markdown, no preamble:
{
  "question": "Your next interview question",
  "questionNumber": 2,
  "isComplete": false,
  "contentScore": 8,
  "contentFeedback": "Detailed 4-6 sentence content evaluation quoting the transcript, addressed to the candidate as you/your",
  "grammarScore": 7,
  "grammarFeedback": "2-3 sentence grammar evaluation with specific quotes, addressed to the candidate as you/your",
  "suggestedAnswer": "Model answer showing what an ideal response looks like",
  "improvementTips": ["Tip 1 with specific example", "Tip 2 with specific example", "Tip 3 with specific example"],
  "overallImpression": "2-sentence honest panel verdict addressed directly to the candidate"
}

For the FIRST question (no answer yet): set contentScore/grammarScore to null, contentFeedback/grammarFeedback/suggestedAnswer to "", improvementTips to [], overallImpression to "". Ask a warm but formal opening question specific to the scholarship and field.
After 5 questions, set isComplete: true. In overallImpression write a 3-sentence final summary covering strengths, weaknesses, and overall suitability for the scholarship — addressed directly to the candidate.`;

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,600&family=JetBrains+Mono:wght@300;400;500&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  ::selection{background:#1a3a2a44;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-track{background:#f5f0e8;}
  ::-webkit-scrollbar-thumb{background:#2d5a3d55;border-radius:2px;}
  body{background:#f5f0e8;}
  .page{min-height:100vh;background:#f5f0e8;font-family:'Cormorant Garamond',Georgia,serif;color:#1a1a1a;position:relative;}
  .paper{position:fixed;inset:0;pointer-events:none;opacity:0.35;background-image:repeating-linear-gradient(0deg,transparent,transparent 27px,rgba(180,160,120,0.18) 28px),linear-gradient(135deg,rgba(200,180,140,0.12) 0%,transparent 60%);}
  .crest{width:52px;height:52px;border:2px solid #2d5a3d;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:22px;color:#2d5a3d;flex-shrink:0;background:rgba(45,90,61,0.06);}
  .card{background:rgba(255,253,248,0.93);border:1px solid rgba(45,90,61,0.18);border-radius:3px;box-shadow:0 2px 20px rgba(45,90,61,0.07),0 1px 4px rgba(0,0,0,0.04);position:relative;}
  .card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#2d5a3d,#4a8c5c,#2d5a3d);}
  .flabel{font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;color:#2d5a3d;margin-bottom:8px;display:block;}
  input,select,textarea{background:rgba(255,253,248,0.8);border:1px solid rgba(45,90,61,0.25);color:#1a1a1a;font-family:'Cormorant Garamond',Georgia,serif;font-size:22px;outline:none;transition:border-color 0.2s,box-shadow 0.2s;width:100%;padding:11px 15px;border-radius:2px;}
  input:focus,select:focus,textarea:focus{border-color:#2d5a3d;box-shadow:0 0 0 3px rgba(45,90,61,0.08);}
  select option{background:#f5f0e8;}
  .btn{background:#2d5a3d;color:#f5f0e8;border:none;padding:15px 32px;font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:500;letter-spacing:0.12em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;border-radius:2px;width:100%;}
  .btn:hover:not(:disabled){background:#3a7a52;transform:translateY(-1px);box-shadow:0 4px 12px rgba(45,90,61,0.25);}
  .btn:disabled{opacity:0.4;cursor:not-allowed;}
  .btn-o{background:transparent;color:#2d5a3d;border:1px solid rgba(45,90,61,0.4);padding:16px 28px;font-family:'JetBrains Mono',monospace;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;border-radius:2px;}
  .btn-o:hover:not(:disabled){background:rgba(45,90,61,0.06);border-color:#2d5a3d;}
  .btn-o:disabled{opacity:0.3;cursor:not-allowed;}
  .btn-next{background:#2d5a3d;color:#f5f0e8;border:none;padding:18px 36px;font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;border-radius:2px;display:flex;align-items:center;gap:10px;justify-content:center;}
  .btn-next:hover{background:#3a7a52;transform:translateY(-1px);box-shadow:0 4px 16px rgba(45,90,61,0.28);}
  .btn-g{background:transparent;color:#8a7a6a;border:1px solid rgba(45,90,61,0.15);padding:8px 15px;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;border-radius:2px;}
  .btn-g:hover{color:#2d5a3d;border-color:rgba(45,90,61,0.3);}
  .mic{width:76px;height:76px;border-radius:50%;border:2px solid #2d5a3d;background:#f5f0e8;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.2s;font-size:30px;flex-shrink:0;}
  .mic:hover:not(:disabled){background:rgba(45,90,61,0.06);transform:scale(1.04);}
  .mic:disabled{opacity:0.4;cursor:not-allowed;}
  .mic.rec{background:#2d5a3d;border-color:#2d5a3d;animation:pulse 1.5s ease infinite;}
  .mic.rec span{filter:invert(1);}
  @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(45,90,61,0.4);}50%{box-shadow:0 0 0 16px rgba(45,90,61,0);}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes wave{0%,100%{height:4px;}50%{height:22px;}}
  .fade-up{animation:fadeUp 0.35s ease forwards;}
  .waves{display:flex;align-items:center;gap:3px;height:26px;}
  .waves span{display:inline-block;width:3px;border-radius:2px;background:#2d5a3d;animation:wave 0.8s ease infinite;}
  .waves span:nth-child(2){animation-delay:0.1s;}
  .waves span:nth-child(3){animation-delay:0.2s;}
  .waves span:nth-child(4){animation-delay:0.3s;}
  .waves span:nth-child(5){animation-delay:0.4s;}
  .bar-bg{height:7px;background:rgba(45,90,61,0.1);border-radius:3px;overflow:hidden;}@media(max-width:600px){.scores-grid{grid-template-columns:repeat(3,1fr)!important;}}
  .bar-fill{height:100%;border-radius:3px;transition:width 1.2s ease;}
  .tbox{background:rgba(45,90,61,0.04);border:1px solid rgba(45,90,61,0.12);border-radius:2px;padding:18px 20px;font-family:'Cormorant Garamond',serif;font-size:22px;color:#3a3a3a;line-height:1.8;min-height:60px;font-style:italic;}
  .model-box{background:rgba(45,90,61,0.05);border:1px solid rgba(45,90,61,0.18);border-left:3px solid #2d5a3d;border-radius:0 2px 2px 0;padding:18px 20px;}
  .tip-box{background:rgba(180,130,40,0.05);border:1px solid rgba(180,130,40,0.2);border-radius:2px;padding:14px 16px;}
  .fhl{background:rgba(220,100,60,0.15);border-radius:2px;padding:0 3px;color:#c04020;}
  .divider{height:1px;background:linear-gradient(90deg,transparent,rgba(45,90,61,0.2),transparent);margin:24px 0;}
  .err{background:rgba(200,60,40,0.06);border:1px solid rgba(200,60,40,0.2);border-radius:2px;padding:14px 18px;font-family:'JetBrains Mono',monospace;font-size:11px;color:#c03020;line-height:1.6;}
  .khint{font-family:'JetBrains Mono',monospace;font-size:10px;color:#9a8a7a;margin-top:8px;line-height:1.6;}
  .khint a{color:#2d5a3d;text-underline-offset:2px;}
  .tag{display:inline-block;padding:4px 12px;border-radius:2px;font-family:'JetBrains Mono',monospace;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;}
  .tg{background:rgba(45,90,61,0.1);color:#2d5a3d;}
  .ta{background:rgba(180,130,40,0.12);color:#8a6010;}
  .tr{background:rgba(200,60,40,0.1);color:#c03020;}
  .skb{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:#8a7a6a;font-size:15px;padding:0;}
  .skb:hover{color:#2d5a3d;}
  .section-title{font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;color:#2d5a3d;margin-bottom:14px;display:flex;align-items:center;gap:8px;}
  .section-title::after{content:'';flex:1;height:1px;background:rgba(45,90,61,0.12);}
  .next-banner{background:linear-gradient(135deg,rgba(45,90,61,0.07),rgba(45,90,61,0.03));border:1px solid rgba(45,90,61,0.2);border-radius:3px;padding:22px 26px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;}

  /* ── Dark mode ── */
  body.dark .page { background: #1b1a19; color: #f3f2f1; }
  body.dark .card { background: #252423; border-color: rgba(255,255,255,0.08); box-shadow: 0 2px 20px rgba(0,0,0,0.3); }
  body.dark input, body.dark select, body.dark textarea { background: #1b1a19; border-color: rgba(255,255,255,0.12); color: #f3f2f1; }
  body.dark input:focus, body.dark select:focus, body.dark textarea:focus { border-color: #2d5a3d; box-shadow: 0 0 0 3px rgba(45,90,61,0.2); }
  body.dark select option { background: #252423; }
  body.dark .tbox { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.08); color: #d4cfc8; }
  body.dark .model-box { background: rgba(45,90,61,0.1); border-color: rgba(45,90,61,0.3); }
  body.dark .tip-box { background: rgba(180,130,40,0.08); border-color: rgba(180,130,40,0.2); }
  body.dark .next-banner { background: rgba(45,90,61,0.12); border-color: rgba(45,90,61,0.25); }
  body.dark .err { background: rgba(200,60,40,0.1); border-color: rgba(200,60,40,0.25); }
  body.dark .btn-o { color: #4a8c5c; border-color: rgba(45,90,61,0.4); }
  body.dark .btn-o:hover:not(:disabled) { background: rgba(45,90,61,0.15); }
  body.dark .btn-g { color: #a09880; border-color: rgba(255,255,255,0.1); }
  body.dark .btn-g:hover { color: #4a8c5c; }
  body.dark .mic { background: #252423; border-color: #2d5a3d; }
  body.dark .section-title { color: #4a8c5c; }
  body.dark .section-title::after { background: rgba(255,255,255,0.08); }
  body.dark .divider { background: linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent); }
  body.dark .flabel { color: #4a8c5c; }
  body.dark .khint { color: #6a5a4a; }
  body.dark ::-webkit-scrollbar-track { background: #1b1a19; }
  body.dark ::-webkit-scrollbar-thumb { background: rgba(45,90,61,0.4); }
  body.dark .bar-bg { background: rgba(255,255,255,0.06); }
  body.dark .tag { background: rgba(255,255,255,0.06); }
  body.dark .tg { background: rgba(45,90,61,0.15); color: #4a8c5c; }
  body.dark .ta { background: rgba(180,130,40,0.15); color: #c8a030; }
  body.dark .tr { background: rgba(200,60,40,0.15); color: #e06050; }
  body.dark .crest { border-color: #4a8c5c; color: #4a8c5c; background: rgba(45,90,61,0.12); }
  body.dark .card::before { background: linear-gradient(90deg,#2d5a3d,#4a8c5c,#2d5a3d); }
  body.dark .paper { opacity: 0.08; }
  body.dark .btn { background: #252423; color: #f3f2f1; border-color: rgba(255,255,255,0.1); }
  body.dark .btn:hover:not(:disabled) { background: #323130; }
  body.dark .btn-next { background: #2d5a3d; }
  body.dark .btn-next:hover { background: #3a7a52; }
  body.dark { --header-bg: rgba(30,28,26,0.95); --score-bg: rgba(255,255,255,0.04); --verdict-bg: rgba(45,90,61,0.1); --result-bg: #252423; }
  body.dark .khint a { color: #4a8c5c; }
  body.dark ::-webkit-scrollbar-track { background: #1b1a19; }
  body.dark ::-webkit-scrollbar-thumb { background: rgba(45,90,61,0.4); }

  /* Inline style overrides via targeted selectors */
  body.dark .interview-label { color: #4a8c5c !important; }
  body.dark .interview-muted { color: #7a7068 !important; }
  body.dark .interview-text { color: #d4cfc8 !important; }
  body.dark .interview-subtext { color: #9a9088 !important; }
  body.dark .interview-stat-num { color: #4a8c5c !important; }
  body.dark .interview-stat-label { color: #7a7068 !important; }
  body.dark .interview-question { color: #f0ede8 !important; }
  body.dark .interview-feedback { color: #c8c0b8 !important; }
  body.dark .interview-suggestion { color: #d4cfc8 !important; }
  body.dark .interview-tip-num { color: #c8a030 !important; }
  body.dark .interview-tip-text { color: #c8b898 !important; }
  body.dark .interview-verdict { color: #d4cfc8 !important; }
  body.dark .interview-next-label { color: #4a8c5c !important; }
  body.dark .interview-next-sub { color: #9a9088 !important; }
  body.dark .interview-rec-text { color: #9a9088 !important; }
  body.dark .interview-score-label { color: #7a7068 !important; }
  body.dark .interview-filler-chip { background: rgba(200,60,40,0.12) !important; border-color: rgba(200,60,40,0.25) !important; color: #e06050 !important; }
  body.dark .interview-warning { background: rgba(200,60,40,0.08) !important; border-color: rgba(200,60,40,0.15) !important; }
  body.dark .interview-warning-text { color: #e06050 !important; }
  body.dark .interview-section-border { border-color: rgba(255,255,255,0.06) !important; }
  body.dark .interview-q-num { background: #3a7a52 !important; }
  body.dark .interview-tip-circle { background: rgba(180,130,40,0.15) !important; border-color: rgba(180,130,40,0.3) !important; }
  body.dark .interview-nav-scholarship { color: #4a8c5c !important; }
  body.dark .interview-nav-uni { color: #9a9088 !important; }
  body.dark .interview-dot-done { background: #4a8c5c !important; }
  body.dark .interview-dot-current { background: #6aac7c !important; }
  body.dark .interview-dot-pending { background: rgba(255,255,255,0.1) !important; }
  body.dark .interview-results-stat { background: #252423 !important; }
  body.dark .interview-grade { color: inherit !important; }
  body.dark .interview-result-row { background: #252423 !important; border-color: rgba(255,255,255,0.06) !important; }
  body.dark .interview-expand-btn { color: #7a7068 !important; }
  body.dark .interview-expanded-section { border-color: rgba(255,255,255,0.06) !important; }
  body.dark .interview-loading { color: #7a7068 !important; }
  body.dark .interview-spinner { border-color: rgba(45,90,61,0.3) !important; border-top-color: #4a8c5c !important; }

  /* Catch-all: override hardcoded dark text colors in dark mode */
  body.dark .page p,
  body.dark .page span:not(.tag):not(.tg):not(.ta):not(.tr):not(.rec-dot),
  body.dark .page div:not(.card):not(.paper):not(.crest):not(.mic):not(.bar-bg):not(.bar-fill):not(.waves):not(.waves span):not(.tbox):not(.model-box):not(.tip-box):not(.next-banner):not(.err):not(.btn):not(.btn-o):not(.btn-g):not(.btn-next):not(.btn-next):not(.section-title):not(.flabel) {
    color: inherit;
  }
  body.dark .page { color: #f3f2f1; }
  body.dark .card { color: #f3f2f1; }
  body.dark .page p { color: #c8c0b8; }
  body.dark .card p { color: #c8c0b8; }
  body.dark .page h1 { color: #f3f2f1; }
  body.dark .card h1, body.dark .card h2, body.dark .card h3 { color: #f3f2f1; }
  body.dark .page em { color: #d4cfc8; }
  body.dark .card em { color: #d4cfc8; }

  /* Override hardcoded inline text colors */
  body.dark .card p[style*="color"] { color: #c8c0b8 !important; }
  body.dark .card div[style*="color: \"#1a1a1a\""] { color: #f3f2f1 !important; }
  body.dark .card div[style*="color: \"#2a2a2a\""] { color: #c8c0b8 !important; }
  body.dark .card div[style*="color: \"#5a4a3a\""] { color: #9a9088 !important; }
  body.dark .card div[style*="color: \"#8a7a6a\""] { color: #7a7068 !important; }
  body.dark .card span[style*="color: \"#8a7a6a\""] { color: #7a7068 !important; }
  body.dark .card div[style*="color: \"#4a3a1a\""] { color: #c8b898 !important; }
  body.dark .card div[style*="color: \"#1a3a2a\""] { color: #a8c8b0 !important; }
  body.dark .card div[style*="color: \"#1a2a1a\""] { color: #c8c0b8 !important; }

  /* Results screen specific */
  body.dark .page div[style*="background: \"#fdfaf4\""] { background: #252423 !important; }
  body.dark .page div[style*="borderTop: \"1px solid rgba(45,90,61"] { border-color: rgba(255,255,255,0.06) !important; }
  body.dark .page button[style*="background: \"transparent\""] { color: #c8c0b8 !important; }
  body.dark .page div[style*="border: \"1px solid rgba(45,90,61,0.1)\""] { border-color: rgba(255,255,255,0.07) !important; }
`;

function detectFillers(text) {
  const lower = text.toLowerCase();
  const found = [];
  FILLER_WORDS.forEach(fw => {
    const matches = lower.match(new RegExp(`\\b${fw}\\b`, "gi"));
    if (matches) found.push({ word: fw, count: matches.length });
  });
  return found;
}
function highlightFillers(text) {
  let r = text;
  FILLER_WORDS.forEach(fw => { r = r.replace(new RegExp(`\\b(${fw})\\b`, "gi"), `<mark class="fhl">$1</mark>`); });
  return r;
}
function calcWpm(text, secs) {
  if (!secs) return 0;
  return Math.round((text.trim().split(/\s+/).filter(Boolean).length / secs) * 60);
}
// function scoreConf(w, fillers, words) {
//   let s = 10;
//   if (w < 80 || w > 200) s -= 2; else if (w < 100 || w > 175) s -= 1;
//   const r = words > 0 ? fillers / words : 0;
//   if (r > 0.1) s -= 3; else if (r > 0.05) s -= 2; else if (r > 0.02) s -= 1;
//   return Math.max(1, Math.min(10, s));
// }
// function scoreCalm(w, pauses, words) {
//   let s = 10;
//   if (w > 180) s -= 3; else if (w > 160) s -= 1;
//   if (words > 20 && pauses === 0) s -= 1;
//   return Math.max(1, Math.min(10, s));
// }
// function scoreVocal(w, words) {
//   let s = 10;
//   if (words < 20) s -= 3; else if (words < 40) s -= 1;
//   if (w > 190) s -= 2;
//   return Math.max(1, Math.min(10, s));
// }

function scoreConf(wpm, fillers, words) {
  let s = 10;
  if (words < 40) s -= 4;
  else if (words < 70) s -= 2;

  const ratio = words > 0 ? fillers / words : 0;
  if (ratio > 0.10) s -= 4;
  else if (ratio > 0.05) s -= 2;
  else if (ratio > 0.02) s -= 1;

  if (wpm < 100 || wpm > 180) s -= 1;
  return Math.max(1, Math.min(10, s));
}

function scoreCalm(wpm, pauses, words) {
  let s = 10;
  if (words < 30) return 5;
  const wordsPerPause = pauses > 0 ? words / pauses : words;
  if (wordsPerPause < 8) s -= 3;
  if (wordsPerPause > 25) s -= 3;

  if (wpm > 175) s -= 2;
  return Math.max(1, Math.min(10, s));
}

function scoreVocal(words, seconds) {
  let s = 10;
  if (seconds < 25) s -= 5;
  else if (seconds < 45) s -= 2;
  else if (seconds > 130) s -= 2;
  if (words < 50) s -= 3;
  if (words > 250) s -= 1;
  return Math.max(1, Math.min(10, s));
}
function mc(score) { return score >= 8 ? "#2d5a3d" : score >= 6 ? "#8a7010" : score >= 4 ? "#c06010" : "#c03020"; }
function scoreLabel(score) { return score >= 9 ? "Excellent" : score >= 8 ? "Strong" : score >= 6 ? "Satisfactory" : score >= 4 ? "Needs Work" : "Weak"; }

export default function Interview() {
  const [apiKey, setApiKey] = useState(() => getGroqKey());
  const [keyInput, setKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [keyConfirmed, setKeyConfirmed] = useState(() => !!getGroqKey());
  const [university, setUniversity] = useState("");
  const [scholarship, setScholarship] = useState("");
  const [applicant, setApplicant] = useState("");
  const [field, setField] = useState("");
  const [screen, setScreen] = useState("setup");
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem("interview_model") || "llama-3.3-70b-versatile");
  const [question, setQuestion] = useState("");
  const [qNum, setQNum] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isRec, setIsRec] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recStart, setRecStart] = useState(null);
  const [recDur, setRecDur] = useState(0);
  const [lastM, setLastM] = useState(null);
  const [lastEval, setLastEval] = useState(null);
  // showEval: true = show evaluation + Next button; false = show recording card
  const [showEval, setShowEval] = useState(false);
  const [results, setResults] = useState([]);
  const [expandedResult, setExpandedResult] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const bottomRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sync = () => {
      const k = getGroqKey();
      setApiKey(k);
      setKeyConfirmed(!!k);
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session");
    if (!sessionId) return;
    const saved = loadSession(sessionId);
    if (!saved) return;
    if (saved.results?.length) setResults(saved.results);
    if (saved.scholarship) setScholarship(saved.scholarship);
    if (saved.university) setUniversity(saved.university);
    if (saved.applicant) setApplicant(saved.applicant);
    if (saved.field) setField(saved.field);
    setScreen("results");
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [question, lastM, loading, transcribing, showEval]);

  useEffect(() => {
    if (question && screen === "interview") {
      speakQuestion(question);
    }
  }, [question, screen]);


  const saveKey = () => {
    const t = keyInput.trim();
    if (!t.startsWith("gsk_")) { setKeyError("Groq keys start with gsk_ — please check."); return; }
    setApiKey(t); saveGroqKey(t); setKeyConfirmed(true); setKeyError("");
  };
  const forgetKey = () => {
    import("../History").then(h => {
      h.clearGroqKey();
      setApiKey("");
      setKeyInput("");
      setKeyConfirmed(false);
      setScreen("setup");
    });
  };

  const startRec = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRec(true); setTranscript(""); setRecStart(Date.now());
    } catch (e) {
      setApiError("Microphone access denied. Please allow microphone permissions.");
    }
  }, []);

  const stopRec = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder) return;
    const dur = recStart ? (Date.now() - recStart) / 1000 : 0;
    setRecDur(dur); setIsRec(false); setTranscribing(true);
    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      formData.append("model", "whisper-large-v3");
      formData.append("response_format", "text");
      try {
        const res = await fetch("https://api.groq.com/openai/v1/audio/transcriptions", {
          method: "POST",
          headers: { "Authorization": `Bearer ${apiKey}` },
          body: formData,
        });
        if (!res.ok) throw new Error(`Whisper error ${res.status}`);
        const text = await res.text();
        setTranscript(text.trim());
      } catch (e) { setApiError("Transcription failed: " + e.message); }
      setTranscribing(false);
      mediaRecorder.stream.getTracks().forEach(t => t.stop());
    };
    mediaRecorder.stop();
  }, [apiKey, recStart]);

  const callAPI = async (userContent, hist) => {
    const seed = Math.random();
    const messages = [
      { role: "system", content: SYSTEM_PROMPT },
      ...(hist.length === 0
        ? [{ role: "user", content: `Scholarship: ${scholarship}\nUniversity: ${university}\nCandidate: ${applicant || "the applicant"}\nField of Study: ${field || "not specified"}\nSession seed: ${seed}\n\nBegin the interview.` }]
        : [...hist, { role: "user", content: userContent }])
    ];
    const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiKey}` },
      body: JSON.stringify({ model: "llama-3.3-70b-versatile", max_tokens: 2000, temperature: 1.0, messages }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error("Invalid API key.");
      throw new Error(err?.error?.message || `HTTP ${res.status}`);
    }
    const data = await res.json();
    const text = data.choices?.[0]?.message?.content || "";
    try { return JSON.parse(text.replace(/```json|```/g, "").trim()); }
    catch { return { question: text, questionNumber: 1, isComplete: false, contentScore: null, grammarScore: null, contentFeedback: "", grammarFeedback: "", suggestedAnswer: "", improvementTips: [], overallImpression: "" }; }
  };

  const startInterview = async () => {
    if (!university || !scholarship) return;
    setLoading(true); setScreen("interview"); setApiError(""); setShowEval(false);
    try {
      const r = await callAPI("", []);
      setQuestion(r.question); setQNum(1);
      setHistory([
        { role: "user", content: `Scholarship: ${scholarship}\nUniversity: ${university}\nCandidate: ${applicant || "the applicant"}\nField of Study: ${field || "not specified"}\n\nBegin the interview.` },
        { role: "assistant", content: JSON.stringify(r) },
      ]);
    } catch (e) { setApiError(e.message); if (e.message.includes("Invalid")) forgetKey(); }
    setLoading(false);
  };

  const speakQuestion = (text) => {
    window.speechSynthesis.cancel();


    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();

      const femaleVoice = voices.find(v =>
        v.name.includes("Google UK English Female") ||
        v.name.includes("Samantha") ||
        v.name.includes("Microsoft Zira") ||
        v.name.includes("Female")
      );

      if (femaleVoice) utterance.voice = femaleVoice;

      utterance.rate = 0.78;
      utterance.pitch = 1.05;
      utterance.volume = 1;

      window.speechSynthesis.speak(utterance);
    }, 300);
  };

  const submitAnswer = async () => {
    if (!transcript.trim() || loading) return;
    setLoading(true); setApiError("");
    setLastM(null); setLastEval(null); setShowEval(false);
    const spoken = transcript.trim();
    const fillers = detectFillers(spoken);
    const fillerCount = fillers.reduce((a, b) => a + b.count, 0);
    const pauseCount = (spoken.match(/[,;]|\.\.\.|—/g) || []).length;
    const wordCount = spoken.trim().split(/\s+/).filter(Boolean).length;
    const w = calcWpm(spoken, recDur);
    const confidence = scoreConf(w, fillerCount, wordCount);
    const calmness = scoreCalm(w, pauseCount, wordCount);
    // const vocal = scoreVocal(w, wordCount);
    const vocal = scoreVocal(wordCount, recDur);
    const userContent = `Transcript: "${spoken}"\n\nSpeech metrics — WPM: ${w}, Filler words detected: ${fillerCount} (${fillers.map(f => f.word).join(", ") || "none"}), Natural pauses: ${pauseCount}, Word count: ${wordCount}`;
    try {
      const r = await callAPI(userContent, history);
      const newHistory = [...history, { role: "user", content: userContent }, { role: "assistant", content: JSON.stringify(r) }];
      setHistory(newHistory);
      const m = { fillers, fillerCount, pauseCount, wpm: w, wordCount, confidence, calmness, vocal, transcript: spoken };
      setLastM(m);
      setLastEval({
        contentScore: r.contentScore != null ? Number(r.contentScore) : null,
        grammarScore: r.grammarScore != null ? Number(r.grammarScore) : null,
        contentFeedback: r.contentFeedback,
        grammarFeedback: r.grammarFeedback,
        suggestedAnswer: r.suggestedAnswer,
        improvementTips: r.improvementTips || [],
        overallImpression: r.overallImpression,
        nextQuestion: r.question,
        nextQNum: r.questionNumber ?? (qNum + 1),
        isComplete: r.isComplete,
      });
      if (r.contentScore !== null) {
        setResults(prev => [...prev, { q: qNum, questionText: question, transcript: spoken, metrics: m, contentScore: r.contentScore, grammarScore: r.grammarScore, contentFeedback: r.contentFeedback, grammarFeedback: r.grammarFeedback, suggestedAnswer: r.suggestedAnswer, improvementTips: r.improvementTips || [], impression: r.overallImpression }]);
      }
      setTranscript(""); setRecDur(0);
      // Show evaluation + Next/Finish button — don't auto-advance
      setShowEval(true);
    } catch (e) { setApiError(e.message); if (e.message.includes("Invalid")) forgetKey(); }
    setLoading(false);
  };
  // Clear the state for the current answer attempt
  const retakeQuestion = () => {
    setLastM(null);
    setLastEval(null);
    setShowEval(false);
    setTranscript("");
    setRecDur(0);

    setResults(prev => prev.filter(r => r.q !== qNum));
    setHistory(prev => prev.slice(0, -2));

  };

  // Called when user clicks "Next Question" or "View Results"
  const goNext = () => {
    if (!lastEval) return;
    if (lastEval.isComplete) {
      const sessionId = Date.now().toString();
      saveSession({
        tool: "interview",
        title: `${scholarship} — ${university}`,
        subtitle: `Grade: ${grade()}`,
        id: sessionId,
      });
      saveFullSession(sessionId, {
        results,
        scholarship,
        university,
        applicant,
        field,
      });
      localStorage.setItem("lliuq_last_interview", JSON.stringify({
        scholarship, university, applicant, field, results, grade: grade()
      }));
      setScreen("results");

    } else {
      setQuestion(lastEval.nextQuestion);
      setQNum(lastEval.nextQNum);
      setLastM(null);
      setLastEval(null);
      setShowEval(false);
    }
  };

  const resetAll = () => {
    setScreen("setup"); setResults([]); setHistory([]); setQuestion(""); setQNum(0);
    setTranscript(""); setLastM(null); setLastEval(null); setApiError("");
    setExpandedResult(null); setShowEval(false);
  };

  const retrySession = () => {
    setResults([]); setHistory([]); setQuestion(""); setQNum(0);
    setTranscript(""); setLastM(null); setLastEval(null); setApiError("");
    setExpandedResult(null); setShowEval(false);
    startInterview();
  };

  const avgScore = (key) => { const v = results.map(r => r[key]).filter(Boolean); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : "—"; };
  const avgM = (key) => { const v = results.map(r => r.metrics[key]).filter(Boolean); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : "—"; };
  const grade = () => {
    const v = results.flatMap(r => [r.contentScore, r.grammarScore, r.metrics.confidence, r.metrics.calmness, r.metrics.vocal].filter(Boolean));
    if (!v.length) return "—";
    const a = v.reduce((x, y) => x + y, 0) / v.length;
    return a >= 9 ? "Distinction" : a >= 7.5 ? "Merit" : a >= 6 ? "Pass" : "Needs Work";
  };
  const gradeCol = () => { const g = grade(); return g === "Distinction" ? "#2d5a3d" : g === "Merit" ? "#8a7010" : g === "Pass" ? "#c06010" : "#c03020"; };
  const BG = <div className="paper" />;


  // ── KEY SCREEN ──
  if (!keyConfirmed) return (
    <div className="page"><style>{STYLES}</style>{BG}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
        <div className="card fade-up" style={{ maxWidth: 500, width: "100%", padding: "52px 44px", textAlign: "center" }}>
          <div className="crest" style={{ margin: "0 auto 20px" }}>🎓</div>
          <h1 style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1, marginBottom: 16 }}>Key Required</h1>
          <p style={{ fontSize: 22, color: "#5a4a3a", lineHeight: 1.8, marginBottom: 26 }}>Please set your Groq API key in the navigation bar or on the Home page to start your interview coach session.</p>
          <button className="btn" onClick={() => window.location.href = "/"}>Go to Home Page</button>
        </div>
      </div>
    </div>
  );

  // ── SETUP SCREEN ──
  if (screen === "setup") return (
    <div className="page"><style>{STYLES}</style>{BG}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
        <div className="card fade-up" style={{ maxWidth: 540, width: "100%", padding: "52px 44px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 34 }}>
            <div className="crest">🎓</div>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: "0.2em", color: "#2d5a3d", textTransform: "uppercase", marginBottom: 5 }}>Scholarship Interview Preparation</div>
              <h1 style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1 }}>Schedule Your <em>Interview</em></h1>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div><span className="flabel">University / Institution</span><input value={university} onChange={e => setUniversity(e.target.value)} placeholder="e.g. Multimedia University (MMU)" /></div>
            <div><span className="flabel">Scholarship Name</span><input value={scholarship} onChange={e => setScholarship(e.target.value)} placeholder="e.g. Allan and Nesta Ferguson Charitable Scholarship" /></div>
            <div><span className="flabel">Your Name <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 10, color: "#8a7a6a", fontWeight: 400 }}>(optional)</span></span><input value={applicant} onChange={e => setApplicant(e.target.value)} placeholder="e.g. John Smith" /></div>
            <div><span className="flabel">Field of Study <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 10, color: "#8a7a6a", fontWeight: 400 }}>(optional)</span></span><input value={field} onChange={e => setField(e.target.value)} placeholder="e.g. Computer Science, Medicine, Law..." /></div>
          </div>
          <div className="divider" />


          <div style={{ marginBottom: 16 }}>
            <span className="flabel" style={{ marginBottom: 10 }}>AI Model — switch if quota is exhausted</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              {[
                { id: "llama-3.3-70b-versatile", label: "Llama 3.3 70B", sub: "Best quality" },
                { id: "meta-llama/llama-4-scout-17b-16e-instruct", label: "Llama 4 Scout", sub: "Smart" },
              ].map(m => (
                <div key={m.id}
                  onClick={() => { setSelectedModel(m.id); localStorage.setItem("interview_model", m.id); }}
                  style={{
                    border: `1px solid ${selectedModel === m.id ? "rgba(45,90,61,0.5)" : "rgba(45,90,61,0.15)"}`,
                    background: selectedModel === m.id ? "rgba(45,90,61,0.06)" : "transparent",
                    borderRadius: 2, padding: "10px 12px", cursor: "pointer",
                    textAlign: "center", transition: "all 0.15s",
                  }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, color: selectedModel === m.id ? "#2d5a3d" : "#5a4a3a" }}>{m.label}</div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "#8a7a6a", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.08em" }}>{m.sub}</div>
                </div>
              ))}
            </div>
          </div>



          <button className="btn" onClick={startInterview} disabled={!university || !scholarship || loading}>{loading ? "Preparing your panel..." : "Begin Interview →"}</button>
          <div style={{ marginTop: 28, display: "flex", gap: 28, flexWrap: "wrap" }}>
            {[["5", "Questions"], ["6", "Metrics"], ["Whisper", "Transcription"], ["Model", "Answers"]].map(([v, l]) => (
              <div key={l}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 24, color: "#2d5a3d", fontWeight: 700 }}>{v}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ── INTERVIEW SCREEN ──
  if (screen === "interview") return (
    <div className="page"><style>{STYLES}</style>{BG}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "var(--header-bg, rgba(245,240,232,0.95))", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(45,90,61,0.12)", padding: "13px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", letterSpacing: "0.15em", textTransform: "uppercase" }}>{scholarship}</div>
            <div style={{ fontSize: 15, color: "#5a4a3a", marginTop: 2 }}>{university}{applicant ? ` · ${applicant}` : ""}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#8a7a6a", marginBottom: 6 }}>Q{qNum} of 5</div>
            <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
              {[1, 2, 3, 4, 5].map(n => <div key={n} style={{ width: 9, height: 9, borderRadius: "50%", background: n < qNum ? "#2d5a3d" : n === qNum ? "#4a8c5c" : "rgba(45,90,61,0.15)", transition: "all 0.3s" }} />)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 24px 80px" }}>

        {/* Question */}
        <div className="card fade-up" style={{ padding: "30px 34px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#2d5a3d", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#f5f0e8", fontWeight: 500 }}>{qNum}</span>
            </div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", letterSpacing: "0.18em", textTransform: "uppercase" }}>Panel Question</div>
          </div>
          {loading && !question
            ? <div style={{ display: "flex", gap: 10, alignItems: "center" }}><div style={{ width: 15, height: 15, border: "2px solid #2d5a3d", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a" }}>Panel is preparing...</span></div>
            : <p style={{ fontSize: 28, lineHeight: 1.65, color: "#1a1a1a", fontStyle: "italic", fontWeight: 600 }}>{question}</p>
          }
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <button onClick={() => speakQuestion(question)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#2d5a3d", opacity: 0.7, padding: 0, display: "flex", alignItems: "center", gap: 6 }} title="Listen to question again">
              🔊 <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase" }}>Replay</span>
            </button>
          </div>



        </div>



        {/* Evaluation */}
        {lastM && lastEval && showEval && (
          <div className="card fade-up" style={{ padding: "28px 32px", marginBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#2d5a3d", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 22, paddingBottom: 14, borderBottom: "1px solid rgba(45,90,61,0.1)" }}>
              Panel Evaluation — Question {qNum}
            </div>

            {/* Scores */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 28 }}>
              {[["Content", lastEval.contentScore], ["Grammar", lastEval.grammarScore], ["Confidence", lastM.confidence], ["Calmness", lastM.calmness], ["Vocal", lastM.vocal]].map(([l, s]) => (
                <div key={l} style={{ background: "var(--score-bg, rgba(45,90,61,0.04))", border: "1px solid rgba(45,90,61,0.1)", borderRadius: 2, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#8a7a6a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 7 }}>{l}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: mc(s) }}>{s}</div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: mc(s), marginTop: 4 }}>{scoreLabel(s)}</div>
                  <div className="bar-bg" style={{ marginTop: 7 }}><div className="bar-fill" style={{ width: `${s * 10}%`, background: mc(s) }} /></div>
                </div>
              ))}
            </div>

            {lastM.wordCount < 50 && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(200,60,40,0.05)', border: '1px solid rgba(200,60,40,0.1)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#c03020", textTransform: "uppercase" }}>
                  Panel Note: Response is brief. Aim for at least 60 seconds to demonstrate depth.
                </span>
              </div>
            )}

            {/* Stats */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
              <span className={`tag ${lastM.wpm >= 100 && lastM.wpm <= 170 ? "tg" : "ta"}`}>{lastM.wpm} WPM</span>
              <span className={`tag ${lastM.fillerCount === 0 ? "tg" : lastM.fillerCount <= 3 ? "ta" : "tr"}`}>{lastM.fillerCount} filler word{lastM.fillerCount !== 1 ? "s" : ""}</span>
              <span className="tag tg">{lastM.wordCount} words spoken</span>
              {lastM.pauseCount > 0 && <span className="tag tg">{lastM.pauseCount} natural pause{lastM.pauseCount !== 1 ? "s" : ""}</span>}
            </div>

            {/* Fillers */}
            {lastM.fillerCount > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div className="section-title">Filler Words Detected</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {lastM.fillers.map(({ word, count }) => (
                    <span key={word} style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, background: "rgba(200,60,40,0.08)", border: "1px solid rgba(200,60,40,0.2)", borderRadius: 2, padding: "4px 12px", color: "#c03020" }}>"{word}" ×{count}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Content feedback */}
            {lastEval.contentFeedback && (
              <div style={{ marginBottom: 22 }}>
                <div className="section-title">Content & Relevance Feedback</div>
                <p style={{ fontSize: 22, color: "#2a2a2a", lineHeight: 1.9, fontWeight: 500 }}>{lastEval.contentFeedback}</p>
              </div>
            )}

            {/* Grammar feedback */}
            {lastEval.grammarFeedback && (
              <div style={{ marginBottom: 22 }}>
                <div className="section-title">Grammar & Delivery Feedback</div>
                <p style={{ fontSize: 22, color: "#2a2a2a", lineHeight: 1.9, fontWeight: 500 }}>{lastEval.grammarFeedback}</p>
              </div>
            )}

            {/* Transcript */}
            <div style={{ marginBottom: 22 }}>
              <div className="section-title">Your Transcript</div>
              <div className="tbox" dangerouslySetInnerHTML={{ __html: highlightFillers(lastM.transcript) }} />
            </div>

            {/* Model answer */}
            {lastEval.suggestedAnswer && (
              <div style={{ marginBottom: 22 }}>
                <div className="section-title">Model Answer — How a Top Candidate Would Respond</div>
                <div className="model-box">
                  <p style={{ fontSize: 22, color: "#1a3a2a", lineHeight: 1.9, fontStyle: "italic", fontWeight: 500 }}>{lastEval.suggestedAnswer}</p>
                </div>
              </div>
            )}

            {/* Tips */}
            {lastEval.improvementTips && lastEval.improvementTips.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div className="section-title">3 Things to Practise Before Next Time</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {lastEval.improvementTips.map((tip, i) => (
                    <div key={i} className="tip-box" style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(180,130,40,0.15)", border: "1px solid rgba(180,130,40,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#8a6010", fontWeight: 500 }}>{i + 1}</span>
                      </div>
                      <p style={{ fontSize: 19, color: "#4a3a1a", lineHeight: 1.8, fontWeight: 500 }}>{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verdict */}
            {lastEval.overallImpression && (
              <div style={{ padding: "16px 20px", background: "var(--verdict-bg, rgba(45,90,61,0.05))", borderLeft: "3px solid #2d5a3d", borderRadius: "0 2px 2px 0", marginBottom: 26 }}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "#2d5a3d", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>Panel Verdict</div>
                <p style={{ fontSize: 22, color: "#1a2a1a", lineHeight: 1.85, fontStyle: "italic", fontWeight: 500 }}>{lastEval.overallImpression}</p>
              </div>
            )}

            {/* ── NEXT / FINISH BUTTON ── */}
            <div className="next-banner">
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#2d5a3d", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
                  {lastEval.isComplete ? "Interview Complete" : `Up Next: Question ${qNum + 1} of 5`}
                </div>
                <div style={{ fontSize: 18, color: "#5a4a3a", fontStyle: "italic" }}>
                  {lastEval.isComplete ? "Review your full results and overall grade." : "When you're ready, move on to the next question."}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 15 }}>
                {/* The new Retake button */}
                <button className="btn-o" onClick={retakeQuestion} style={{ flex: 1 }}>
                  ↺ Retake Question
                </button>

                {/* Your original button untouched */}
                <button className="btn-next" onClick={goNext} style={{ flex: 1.5 }}>
                  {lastEval.isComplete ? "View Full Results →" : `Next Question →`}
                </button>
              </div>
            </div>
          </div>
        )}

        {apiError && <div className="err fade-up" style={{ marginBottom: 14 }}>{apiError}</div>}

        {/* Recording — only shown when NOT showing evaluation */}
        {!loading && question && !showEval && (
          <div className="card fade-up" style={{ padding: "26px 30px" }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 18 }}>Your Response</div>
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 20 }}>
              <button className={`mic${isRec ? " rec" : ""}`} onClick={isRec ? stopRec : startRec} disabled={transcribing}>
                <span>{isRec ? "⏹" : "🎤"}</span>
              </button>
              <div>
                {isRec
                  ? <><div className="waves" style={{ marginBottom: 6 }}><span /><span /><span /><span /><span /></div><div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#2d5a3d" }}>Recording — tap ⏹ when done</div></>
                  : transcribing
                    ? <div style={{ display: "flex", gap: 7, alignItems: "center" }}><div style={{ width: 13, height: 13, border: "2px solid #2d5a3d", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#2d5a3d" }}>Transcribing your response...</span></div>
                    : transcript
                      ? <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#5a4a3a" }}>Transcribed. Edit if needed, then submit.</div>
                      : <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a" }}>Tap 🎤 and speak your answer clearly</div>
                }
              </div>
            </div>
            {transcript && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "#8a7a6a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 7 }}>Whisper Transcript <span style={{ color: "#b0a090" }}>(editable)</span></div>
                <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={4} style={{ lineHeight: 1.75, resize: "vertical", fontStyle: "italic" }} />
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn" style={{ flex: 1 }} onClick={submitAnswer} disabled={!transcript.trim() || loading || isRec || transcribing}>
                {loading ? "Panel is evaluating..." : "Submit Answer →"}
              </button>
              {transcript && <button className="btn-o" onClick={() => setTranscript("")}>Clear</button>}
            </div>
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "22px 4px" }}>
            <div style={{ width: 15, height: 15, border: "2px solid #2d5a3d", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a" }}>Panel is reviewing your response and preparing detailed feedback...</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );

  // ── RESULTS SCREEN ──
  return (
    <div className="page"><style>{STYLES}</style>{BG}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "52px 24px 80px" }}>
        <div className="fade-up">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="crest" style={{ margin: "0 auto 20px" }}>🎓</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>Interview Complete — Full Report</div>
            <h1 style={{ fontSize: 52, fontWeight: 600, color: gradeCol(), fontStyle: "italic", marginBottom: 8 }}>{grade()}</h1>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a" }}>{scholarship} · {university}</div>
            {applicant && <div style={{ fontSize: 20, color: "#3a3a3a", marginTop: 6, fontStyle: "italic" }}>{applicant}</div>}
          </div>

          {/* Summary Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: "rgba(45,90,61,0.12)", marginBottom: 36, borderRadius: 3, overflow: "hidden" }}>
            {[["Content", avgScore("contentScore")], ["Grammar", avgScore("grammarScore")], ["Confidence", avgM("confidence")], ["Calmness", avgM("calmness")], ["Vocal Delivery", avgM("vocal")], ["Avg WPM", avgM("wpm")]].map(([l, v]) => (
              <div key={l} style={{ background: "var(--result-bg, #fdfaf4)", padding: "20px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 34, fontWeight: 700, color: "#2d5a3d", fontStyle: "italic" }}>{v}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#8a7a6a", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 5 }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Final verdict */}
          {results.length > 0 && results[results.length - 1].impression && (
            <div style={{ padding: "22px 26px", background: "var(--verdict-bg, rgba(45,90,61,0.05))", borderLeft: "4px solid #2d5a3d", borderRadius: "0 3px 3px 0", marginBottom: 30 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 10 }}>Final Panel Verdict</div>
              <p style={{ fontSize: 22, color: "#1a2a1a", lineHeight: 1.9, fontStyle: "italic", fontWeight: 500 }}>{results[results.length - 1].impression}</p>
            </div>
          )}

          {/* Breakdown */}
          <div className="card" style={{ padding: "26px 30px", marginBottom: 26 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#2d5a3d", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 24 }}>Full Question Breakdown</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {results.map((r, i) => (
                <div key={i} style={{ border: "1px solid rgba(45,90,61,0.1)", borderRadius: 2, overflow: "hidden" }}>
                  <button onClick={() => setExpandedResult(expandedResult === i ? null : i)} style={{ width: "100%", background: expandedResult === i ? "var(--score-bg, rgba(45,90,61,0.05))" : "transparent", border: "none", cursor: "pointer", padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#2d5a3d", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#f5f0e8", fontWeight: 500 }}>{r.q}</span>
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 18, color: "#1a1a1a", fontStyle: "italic", lineHeight: 1.5, fontWeight: 500 }}>{r.questionText}</div>
                        <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                          {[["Content", r.contentScore], ["Grammar", r.grammarScore], ["Confidence", r.metrics.confidence], ["Calmness", r.metrics.calmness], ["Vocal", r.metrics.vocal]].map(([l, s]) => (
                            <span key={l} style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: mc(s), background: `${mc(s)}18`, border: `1px solid ${mc(s)}33`, borderRadius: 2, padding: "2px 7px" }}>{l} {s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#8a7a6a", flexShrink: 0, marginLeft: 10 }}>{expandedResult === i ? "▲ Hide" : "▼ Details"}</span>
                  </button>
                  {expandedResult === i && (
                    <div style={{ padding: "22px 22px 26px", borderTop: "1px solid rgba(45,90,61,0.08)" }}>
                      <div style={{ marginBottom: 18 }}>
                        <div className="section-title">Content Feedback</div>
                        <p style={{ fontSize: 18, color: "#2a2a2a", lineHeight: 1.9, fontWeight: 500 }}>{r.contentFeedback}</p>
                      </div>
                      <div style={{ marginBottom: 18 }}>
                        <div className="section-title">Grammar Feedback</div>
                        <p style={{ fontSize: 18, color: "#2a2a2a", lineHeight: 1.9, fontWeight: 500 }}>{r.grammarFeedback}</p>
                      </div>
                      <div style={{ marginBottom: 18 }}>
                        <div className="section-title">Your Answer</div>
                        <div className="tbox" style={{ fontSize: 15 }} dangerouslySetInnerHTML={{ __html: highlightFillers(r.transcript) }} />
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                          <span className={`tag ${r.metrics.wpm >= 100 && r.metrics.wpm <= 170 ? "tg" : "ta"}`}>{r.metrics.wpm} WPM</span>
                          <span className={`tag ${r.metrics.fillerCount === 0 ? "tg" : r.metrics.fillerCount <= 3 ? "ta" : "tr"}`}>{r.metrics.fillerCount} fillers</span>
                          <span className="tag tg">{r.metrics.wordCount} words</span>
                        </div>
                      </div>
                      {r.suggestedAnswer && (
                        <div style={{ marginBottom: 18 }}>
                          <div className="section-title">Model Answer</div>
                          <div className="model-box"><p style={{ fontSize: 18, color: "#1a3a2a", lineHeight: 1.9, fontStyle: "italic", fontWeight: 500 }}>{r.suggestedAnswer}</p></div>
                        </div>
                      )}
                      {r.improvementTips && r.improvementTips.length > 0 && (
                        <div>
                          <div className="section-title">Improvement Tips</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {r.improvementTips.map((tip, j) => (
                              <div key={j} className="tip-box" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(180,130,40,0.15)", border: "1px solid rgba(180,130,40,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "#8a6010", fontWeight: 500 }}>{j + 1}</span>
                                </div>
                                <p style={{ fontSize: 18, color: "#4a3a1a", lineHeight: 1.8, fontWeight: 500 }}>{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="btn" style={{ flex: 1, minWidth: 200 }} onClick={resetAll}>New Interview →</button>
            <button className="btn-o" style={{ flex: 1, minWidth: 200 }} onClick={retrySession}>Retry Same Scholarship ↺</button>
          </div>
          {/* --- Support Section --- */}
          <div
            className="card fade-up"
            style={{
              marginTop: 40,
              padding: 32,
              textAlign: 'center',
              background: 'rgba(45,90,61,0.03)',
              borderStyle: 'dashed'
            }}
          >
            <div className="section-title" style={{ justifyContent: 'center' }}>Support this Project</div>
            <p style={{ fontSize: 20, marginBottom: 24, color: '#4a4a4a', fontFamily: 'Cormorant Garamond' }}>
              If you enjoy the interview session, consider supporting the project for better experience.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <a
                href="https://ko-fi.com/lliuqdeveloper"
                target="_blank"
                rel="noreferrer"
                className="btn-o"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                ☕ Support on Ko-fi
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}